import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';
import type { User } from '@/_types/models/user';
import type {
  ClientAccess,
  SpecialRoleType,
} from '@/_types/models/clientAccesses';
import type { Company } from '@/_types/models/company';
import type { TaxReturn } from '@/_types/models/taxReturns';
import type { ExportableDocument } from '@/_contexts/ReviewAndExportProvider/ReviewAndExportProvider.types';

// returns all entity types of the firm without adding C/S-Corp placeholders
export const getAllEntityTypes = async ({
  token,
}: APIRequest): APIResponsePromise<{ entity_types: Array<string> }> =>
  await _callBackend('/firms/all_firms_entity_types', 'GET', null, token);

export const getCompanyByHashId = async ({
  token,
  hashId,
}: APIRequest<{ hashId: string }>): APIResponsePromise<{ company: Company }> =>
  await _callBackend(
    `/companies/get_company_by_hash_id?hash_id=${hashId}`,
    'GET',
    null,
    token,
  );

export const getCompanyById = async ({
  token,
  companyId,
}: APIRequest<{ companyId: number }>): APIResponsePromise<Company> =>
  await _callBackend(`/companies/${companyId}`, 'GET', null, token);

export const deleteCompany = async ({
  token,
  id,
}: APIRequest<{ id: number }>): APIResponsePromise =>
  await _callBackend(`/companies/${id}/delete_company`, 'POST', null, token);

export const hideCompanyFromConnect = async ({
  token,
  id,
}: APIRequest<{ id: number }>): APIResponsePromise<{ message: string }> =>
  await _callBackend(`/companies/${id}/hide_from_connect`, 'POST', null, token);

export const showCompanyInConnect = async ({
  token,
  id,
}: APIRequest<{ id: number }>): APIResponsePromise<{ message: string }> =>
  await _callBackend(`/companies/${id}/show_in_connect`, 'POST', null, token);

export const createNewOwner = async ({
  token,
  companyId,
  email,
  firstName,
  lastName,
  phoneNumber,
  advisor,
  copyPreviousMessages,
  transferPreviousMessages,
}: APIRequest<{
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  advisor?: number;
  copyPreviousMessages: boolean;
  transferPreviousMessages: boolean;
}>): APIResponsePromise<User> =>
  await _callBackend(
    `/companies/${companyId}/create_new_owner`,
    'POST',
    {
      email,
      fname: firstName,
      groupId: null,
      lname: lastName,
      phone_number: phoneNumber,
      assigned_advisor_id: advisor,
      should_copy_over_previous_messages: copyPreviousMessages,
      should_transfer_over_previous_messages: transferPreviousMessages,
    },
    token,
  );

export interface UpdateCompanyAttrs {
  business_email?: string | null;
  city?: string | null;
  companyId: number;
  country?: string | null;
  entity_type?: string;
  external_entity_id?: string;
  external_user_id?: string;
  inactive?: boolean | null;
  name?: string;
  other_email?: string | null;
  paying?: boolean;
  readonly_access?: boolean | null;
  state?: string | null;
  street?: string | null;
  userId: number;
  zip?: string | null;
  tax_return_id?: string | null;
  spouse_ssn?: string | null;
}

export const updateCompany = async ({
  business_email,
  city,
  companyId,
  country,
  entity_type,
  external_entity_id,
  external_user_id,
  inactive,
  name,
  other_email,
  paying,
  readonly_access,
  state,
  street,
  token,
  userId,
  zip,
  tax_return_id,
  spouse_ssn,
}: APIRequest<UpdateCompanyAttrs>): APIResponsePromise<Company> =>
  await _callBackend(
    `/companies/${companyId}`,
    'PATCH',
    {
      business_email,
      city,
      country,
      entity_type,
      external_entity_id,
      external_user_id,
      inactive,
      name,
      other_email,
      paying,
      readonly_access,
      state,
      street,
      user_id: userId,
      zip,
      tax_return_id,
      spouse_ssn,
    },
    token,
  );

export interface CreateCompanyAttrs {
  business_email: string | null;
  country: string | null;
  ein: string;
  entity_type: string;
  name: string;
  fax: string;
  firstName: string | null;
  lastName: string | null;
  other_email: string | null;
  state: string | null;
  street: string | null;
  city: string | null;
  user_id: number;
  zip: string | null;
}

export const createCompany = async ({
  business_email,
  city,
  country,
  entity_type,
  name,
  other_email,
  state,
  street,
  token,
  user_id,
  zip,
}: APIRequest<CreateCompanyAttrs>): APIResponsePromise<Company> =>
  await _callBackend(
    '/companies',
    'POST',
    {
      business_email,
      city,
      country,
      entity_type,
      name,
      other_email,
      state,
      street,
      user_id,
      zip,
    },
    token,
  );

export const updateOwners = async ({
  token,
  companyId,
  ownerIds,
  copyPreviousMessages,
  transferPreviousMessages,
  sourceConversationId,
}: APIRequest<{
  companyId: number;
  ownerIds: Array<number>;
  copyPreviousMessages: boolean;
  transferPreviousMessages: boolean;
  sourceConversationId?: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/update_owners`,
    'POST',
    {
      owner_ids: ownerIds,
      should_copy_over_previous_messages: copyPreviousMessages,
      should_transfer_over_previous_messages: transferPreviousMessages,
      source_conversation_id: sourceConversationId,
    },
    token,
  );

export const updatePrimaryContact = async ({
  token,
  newPrimaryContactId,
  companyId,
}: APIRequest<{
  newPrimaryContactId: number;
  companyId: number;
}>): APIResponsePromise<{ message: 'Success!' }> =>
  await _callBackend(
    `/companies/${companyId}/change_primary_contact`,
    'POST',
    {
      new_primary_contact_id: newPrimaryContactId,
    },
    token,
  );

export const getCompanyQuestionnaires = async ({
  token,
  companyId,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<QuestionnaireIndexSerializer>> =>
  await _callBackend(
    `/questionnaires/get_questionnaires_for_year/?company_id=${companyId}&year=All&full=false`,
    'GET',
    null,
    token,
  );

export const getCompanySpouse = async ({
  token,
  companyId,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Partial<User>> =>
  await _callBackend(`/companies/${companyId}/spouse`, 'GET', null, token);

export const getCompanySignatureRequests = async ({
  token,
  companyId,
  year,
  sectionId,
}: APIRequest<{
  companyId: number;
  year?: number;
  sectionId?: number;
}>): APIResponsePromise => {
  const urlParams = new URLSearchParams();

  urlParams.append('company_id', companyId.toString());

  if (year) {
    urlParams.append('year', year.toString());
  }

  if (sectionId) {
    urlParams.append('section_id', sectionId.toString());
  }

  return await _callBackend(
    `/signature_requests/get_signature_requests_by_company_id?${urlParams.toString()}`,
    'GET',
    null,
    token,
  );
};

export const getCompanyTaxReturns = async ({
  token,
  companyId,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<TaxReturn>> =>
  await _callBackend(
    `/tax_returns/get_tax_returns_index?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

export const saveOwnerNotifications = async ({
  companyId,
  ownersWithNotifications,
  token,
}: APIRequest<{
  companyId: number;
  ownersWithNotifications: Array<any>;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/save_owner_notifications`,
    'POST',
    {
      entity_id: companyId,
      owners_with_notifications: ownersWithNotifications,
    },
    token,
  );

export const addCompanySpouse = async ({
  companyId,
  email,
  fname,
  lname,
  is_taxpayer,
  needsAccess,
  phone_number,
  work_email,
  token,
}: APIRequest<{
  companyId: number;
  email: string;
  fname: string;
  lname: string;
  is_taxpayer: boolean;
  needsAccess: boolean;
  phone_number: string;
  work_email: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/spouse`,
    'POST',
    {
      email,
      fname,
      lname,
      is_taxpayer,
      needsAccess,
      phone_number,
      work_email,
    },
    token,
  );

export const removeCompanySpouse = async ({
  companyId,
  spouseId,
  token,
}: APIRequest<{
  spouseId: number;
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/spouse/${spouseId}`,
    'DELETE',
    null,
    token,
  );

export const getCompanyRoles = async ({
  companyId,
  token,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<ClientAccess>> =>
  await _callBackend(`/companies/${companyId}/get_roles`, 'GET', null, token);

export const updateUserRole = async ({
  companyId,
  userId,
  role,
  token,
}: APIRequest<{
  userId: number;
  role: SpecialRoleType;
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/update_roles`,
    'PATCH',
    { special_role: role, user_id: userId },
    token,
  );
export const updateClientAssignment = async ({
  companyId,
  userId,
  role,
  token,
}: APIRequest<{
  userId: number;
  role: SpecialRoleType;
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/companies/${companyId}/update_client_assignment`,
    'PATCH',
    { special_role: role, user_id: userId },
    token,
  );

export const getAllExportableDocumentsForEntity = async ({
  id,
  year,
  token,
}: APIRequest<{
  id: number;
  year: number;
}>): APIResponsePromise<Array<ExportableDocument>> => {
  const response = await _callBackend(
    `/companies/${id}/get_all_exportable_documents_for_entity?year=${year}`,
    'POST',
    null,
    token,
  );

  const documents: Array<unknown> = Array.isArray(response) ? response : [];

  return documents as Array<ExportableDocument>;
};

export const getCompaniesSimpleIndex = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend('/companies/simple_index', 'GET', null, token);
